import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { putSuccessUserDetail as ToastPutSuccess, putErrorUserDetail as ToastPutError } from './../../shared/utils/toast'
import { putUserName } from './api/User'

import { Row, Col, Form, Button, Spinner, Placeholder } from 'react-bootstrap'
import { PencilSquare } from 'react-bootstrap-icons'
import { ConstantEquals, USER } from './../../shared/constants/index'
import { ValidMessage, ValidUsername } from './Validate'

export const Username = (props) => {

  const { userId, doneUpdate, username: propUsername } = props

  // 表示用（確定値）
  const [displayName, setDisplayName] = useState(null)

  // 編集モード / 通信中
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  // 入力とエラー
  const [inputName, setInputName] = useState('')
  const [errors, setErrors] = useState({})

  // 初期＆親更新反映
  useEffect(() => {
    setDisplayName(propUsername ?? null)
    if (!isEditing) {
      setInputName(propUsername ?? '')
      setErrors({})
    }
  }, [propUsername, isEditing])

  // 変更有無
  const hasChanges = useMemo(() => {
    // 余計なスペース差分で無駄な更新を起こさない
    return (inputName ?? '').trim() !== (displayName ?? '').trim()
  }, [inputName, displayName])

  // 単項目バリデーション
  const validateUsername = useCallback((name) => {
    const code = ValidUsername(name)
    if (!ConstantEquals(code, USER.VALIDATE.CLEAR)) {
      return ValidMessage(code)
    }
    return null
  }, [])

  // フォーム全体のバリデーション
  const validateForm = useCallback(() => {
    const nextErrors = {}
    if (isEditing) {
      const msg = validateUsername(inputName)
      if (msg) nextErrors[USER.LABEL.USERNAME] = msg
    }
    return { isValid: Object.keys(nextErrors).length === 0, errors: nextErrors }
  }, [isEditing, inputName, validateUsername])


  // 送信可能判定
  const canSubmit = useMemo(() => {
    if (!isEditing || isLoading) return false
    if (!hasChanges) return false
    return true
  }, [isEditing, isLoading, hasChanges])

  // 編集開始/終了
  const startEdit = () => {
    setIsEditing(true)
    setInputName(displayName ?? '')
    setErrors({})
  }
  const cancelEdit = () => {
    setIsEditing(false)
    setInputName(displayName ?? '')
    setErrors({})
  }

  // 入力変更
  const onChangeInput = (e) => {
    const value = e.target.value
    setInputName(value)

    // 入力時は当該エラーのみクリア
    setErrors((prev) => {
      if (!prev[USER.LABEL.USERNAME]) return prev
      const next = { ...prev }
      delete next[USER.LABEL.USERNAME]
      return next
    })
  }

  // キーボード操作
  const onKeyDown = (e) => {
    // Enterは更新
    if (e.key === 'Enter') {
      e.preventDefault()
      if (canSubmit) onSubmit()
    } 
    // Escapeはキャンセル
    else if (e.key === 'Escape') {
      e.preventDefault()
      cancelEdit()
    }
  }

  // 応答パース
  const parseUpdateUser = (updateUser) => {
    const res = {
      done: false,
      username: null,
      updatedAt: ''
    }

    if (!updateUser || typeof updateUser !== 'object') {
      res.done = true
      return res
    }

    // ユーザ名
    if (ConstantEquals(USER.LABEL.USERNAME, USER.LABEL.USERNAME)) {
      const name = updateUser?.username ?? ''
      const msg = validateUsername(name)
      if (!msg) res.username = name
    }

    // 更新日
    res.updatedAt = updateUser?.updatedAt ?? ''
    res.done = true
    return res
  }

  // 送信
  const onSubmit = async () => {
    const { isValid, errors: nextErrors } = validateForm()
    if (!isValid) {
      setErrors(nextErrors)
      return
    }
    if (!hasChanges) return

    setIsLoading(true)
    try {
      const param = { id: userId, [USER.LABEL.USERNAME]: inputName }
      const resp = await putUserName(param)
      const parsed = parseUpdateUser(resp?.data?.updateUser)

      if (!parsed.done || !parsed.username) {
        ToastPutError()
        return
      }

      // 表示反映
      setDisplayName(parsed.username)
      ToastPutSuccess()
      // 親へ更新日通知
      if (typeof doneUpdate === 'function') {
        doneUpdate(parsed.updatedAt)
      }
      if (typeof props.onChangeUsername === 'function') {
        props.onChangeUsername(parsed.username)
      }

      // 正常終了で編集終了
      setIsEditing(false)
      setErrors({})
    } catch (e) {
      ToastPutError()
    } finally {
      setIsLoading(false)
    }
  }

  // ローディング/未取得時のプレースホルダ
  if (!displayName) {
    return (
      <Row>
        <Col xs={12} md={12} className="mb-4 mt-3">
          <Placeholder as="h3" animation="glow">
            <Placeholder.Button xs={10} md={10} variant="secondary" />
          </Placeholder>
        </Col>
      </Row>
    )
  }

  return (
    <Row>
      <Col xs={12} md={12} className="mt-3 mb-2">
        <h3 className="text-break">
          {isEditing ? (
            <>
              <Row>
                <Col xs={12} md={8} className="mt-3 mb-2">
                  <Form.Control
                    size="lg"
                    type="text"
                    value={inputName}
                    placeholder="ユーザ名を入力"
                    disabled={isLoading}
                    onChange={onChangeInput}
                    onKeyDown={onKeyDown}
                    aria-label="ユーザ名"
                    autoFocus
                  />

                  {!!errors.username && (
                    <div className="text-danger small mt-1" role="alert">
                      {errors.username}
                    </div>

)}
                </Col>
                <Col xs={12} md={4} className="mt-3 mb-2">
                  {isLoading ? (
                    <Button variant="primary" disabled aria-live="polite">
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />{' '}
                      Loading...
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      disabled={!canSubmit}
                      onClick={onSubmit}
                    >
                      変更
                    </Button>
                  )}
                  {' '}
                  <Button
                    variant="secondary"
                    disabled={isLoading}
                    onClick={cancelEdit}
                  >
                    キャンセル
                  </Button>
                </Col>
              </Row>
            </>
          ) : (
            <>
              {displayName}{' '}
              <PencilSquare
                style={{ cursor: 'pointer' }}
                aria-label="ユーザ名を編集"
                title="ユーザ名を編集"
                size={16}
                color="gray"
                onClick={startEdit}
              />
            </>
          )}
        </h3>
      </Col>
    </Row>
  )
}